import React, { useEffect, useRef, useState } from 'react';
import { ChartConfig } from '../types';

interface DirectTradingViewChartProps {
  config: ChartConfig;
  theme: 'dark' | 'light';
  timezone: string;
  locale: string;
  onConfigChange?: (updates: Partial<ChartConfig>) => void;
}

const DirectTradingViewChart: React.FC<DirectTradingViewChartProps> = ({
  config,
  theme,
  timezone,
  locale,
  onConfigChange,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let retryCount = 0;
    const maxRetries = 30;

    const createWidget = () => {
      console.log(`Attempting to create widget for ${config.id}, attempt ${retryCount + 1}`);
      
      if (widgetRef.current) {
        try {
          widgetRef.current.remove();
        } catch (e) {
          console.warn('Error removing existing widget:', e);
        }
      }

      // Check if TradingView is available and is a constructor
      // TradingView might be an object with a widget property or a function
      const TradingViewConstructor = window.TradingView?.widget || window.TradingView;
      if (TradingViewConstructor && typeof TradingViewConstructor === 'function') {
        console.log('TradingView constructor is available, creating widget...');
        
        try {
        const tradingViewConfig = {
          container_id: config.id,
          width: '100%',
          height: '100%',
          symbol: config.symbol,
          interval: config.interval,
          timezone,
          theme,
          style: '1',
          locale,
          toolbar_bg: theme === 'dark' ? '#1f2937' : '#ffffff',
          enable_publishing: true,
          allow_symbol_change: true,
          studies: config.studies,
          withdateranges: true,
          // Enable symbol and timeframe changes within the chart
          symbol_search: true,
          symbol_search_request_delay: 1000,
        };

          console.log('Creating TradingView widget with config:', tradingViewConfig);
          widgetRef.current = new TradingViewConstructor(tradingViewConfig);
          console.log('TradingView widget created successfully for:', config.id);
          
          
          setIsLoading(false);
          setError(null);
        } catch (err) {
          console.error('Error creating TradingView widget:', err);
          setError(err instanceof Error ? err.message : 'Unknown error creating widget');
          setIsLoading(false);
        }
      } else {
        retryCount++;
        if (retryCount < maxRetries) {
        console.log(`TradingView not ready, retrying in 1 second... (${retryCount}/${maxRetries})`);
        console.log('Current state:', {
          tradingViewExists: !!window.TradingView,
          tradingViewType: typeof window.TradingView,
          tradingViewValue: window.TradingView,
          hasWidgetProperty: !!(window.TradingView && window.TradingView.widget),
          widgetType: window.TradingView?.widget ? typeof window.TradingView.widget : 'undefined'
        });
          timeoutId = setTimeout(createWidget, 1000);
        } else {
          console.error('TradingView failed to load after maximum retries');
          setError('TradingView failed to load after maximum retries');
          setIsLoading(false);
        }
      }
    };

    // Start with a longer delay to ensure everything is ready
    timeoutId = setTimeout(createWidget, 2000);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (widgetRef.current) {
        try {
          widgetRef.current.remove();
        } catch (e) {
          console.warn('Error removing widget on cleanup:', e);
        }
      }
    };
  }, [config, theme, timezone, locale]);


  return (
    <div className="trading-view-chart">
      <div className="chart-container">
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner">
              {error ? 'Retrying...' : 'Loading TradingView...'}
            </div>
          </div>
        )}
        {error && (
          <div className="error-overlay">
            <div className="error-message">Error: {error}</div>
          </div>
        )}
        <div ref={chartRef} id={config.id} className="chart" />
      </div>
    </div>
  );
};

export default DirectTradingViewChart;
